// src/app/core/whatfix/whatfix.service.ts
import { Injectable, InjectionToken, inject } from '@angular/core';

export const WHATFIX_API_KEY = new InjectionToken<string>('WHATFIX_API_KEY');

type WhatfixInitPayload = {
  account: { id: string };
  visitor?: { id: string };
};

@Injectable({ providedIn: 'root' })
export class WhatFixService {
  private apiKey = inject(WHATFIX_API_KEY, { optional: true });

  /** Prevent loading the initiator script more than once */
  private injected = false;

  /**
   * Optional: set/override API key at runtime
   * @param key
   */
  setApiKey(key: string): void {
    this.apiKey = key;
  }

  /**
   * Unified initializer:
   * - Call with only tenant: initializeWithContext(tenantId)
   * - Or with tenant + user: initializeWithContext(tenantId, userId)
   * It lazy-inits the Whatfix stub and queues the initialize call.
   * @param tenantId
   * @param userId
   */
  initializeWithContext(tenantId: string, userId?: string): void {
    this.ensureInit();

    const wf = (window as any).whatFix;
    const payload: WhatfixInitPayload = {
      account: { id: String(tenantId) }
    };

    // Only include visitor if userId is available
    if (userId) {
      payload.visitor = { id: String(userId) };
    }

    wf?.initialize?.(payload);
  }

  // -------------------- Private --------------------

  /** Ensures the Whatfix stub + initiator script exist (lazy init once) */
  private ensureInit(): void {
    if (this.injected) return;

    if (!this.apiKey) {
      console.warn('[Whatfix] API key is missing. Provide WHATFIX_API_KEY or call setApiKey().');
      return;
    }

    this.injectStubAndScript(this.apiKey);
    this.injected = true;
  }

  /**
   * Injects the non-Promise stub and the initiator script (idempotent)
   * @param apiKey
   */
  private injectStubAndScript(apiKey: string): void {
    (function (p: any, e: Document, n: string, d: string) {
      let v: string[], w: number, x: number, y: HTMLScriptElement, z: HTMLScriptElement | null;

      const o = (p[d] = p[d] || {});
      if (o.__wfInjected) return;
      o.__wfInjected = true;

      o._q = o._q || [];
      v = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track'];
      for (w = 0, x = v.length; w < x; ++w) {
        ((m: string) => {
          o[m] =
            o[m] ||
            function () {
              o._q[m === v[0] ? 'unshift' : 'push']([m].concat([].slice.call(arguments, 0)));
            };
        })(v[w]);
      }

      y = e.createElement(n) as HTMLScriptElement;
      y.async = true;
      y.src = `https://cdn.whatfix.com/prod/${apiKey}/initiator/initiator.nocache.js`;
      z = e.getElementsByTagName(n)[0] as HTMLScriptElement | null;

      if (z?.parentNode) z.parentNode.insertBefore(y, z);
      else if (e.head) e.head.appendChild(y);
      else e.body.appendChild(y);
    })(window as any, document, 'script', 'whatFix'); // NOTE: global name is case-sensitive
  }
}
